import React, { useState, useEffect } from 'react';
import { Building2, Plus, Edit2, Eye, Play, AlertTriangle, CheckCircle, Clock, ChevronRight, GitBranch } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const KybVerificationLevels = () => {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDecisionTreeModal, setShowDecisionTreeModal] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [decisionNodes, setDecisionNodes] = useState([]);
  const [editingLevel, setEditingLevel] = useState(null);
  const [formData, setFormData] = useState({
    name: '', slug: '', description: '', level_tier: 1, due_diligence_type: 'CDD',
    risk_level: 'MEDIUM', ubo_threshold_percent: 25, require_ubo_kyc: true,
    max_ownership_depth: 5, review_interval_months: 12
  });

  useEffect(() => { fetchLevels(); }, []);

  const fetchLevels = async () => {
    try {
      const res = await api.get('/kyb-levels');
      setLevels(res.data.levels || []);
    } catch (error) {
      console.error('Error fetching KYB levels:', error);
      toast.error('Failed to load KYB levels');
    } finally {
      setLoading(false);
    }
  };

  const fetchDecisionTree = async (levelId) => {
    try {
      const res = await api.get(`/kyb-levels/${levelId}`);
      setDecisionNodes(res.data.level?.nodes || []);
      setSelectedLevel(res.data.level);
      setShowDecisionTreeModal(true);
    } catch (error) {
      console.error('Error fetching decision tree:', error);
      toast.error('Failed to load decision tree');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLevel) {
        await api.put(`/kyb-levels/${editingLevel.id}`, formData);
        toast.success('KYB level updated');
      } else {
        await api.post('/kyb-levels', formData);
        toast.success('KYB level created');
      }
      setShowModal(false);
      resetForm();
      fetchLevels();
    } catch (error) {
      console.error('Error saving level:', error);
      toast.error('Failed to save KYB level');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', slug: '', description: '', level_tier: 1, due_diligence_type: 'CDD',
      risk_level: 'MEDIUM', ubo_threshold_percent: 25, require_ubo_kyc: true,
      max_ownership_depth: 5, review_interval_months: 12
    });
    setEditingLevel(null);
  };

  const handleEdit = (level) => {
    setEditingLevel(level);
    setFormData({
      name: level.name, slug: level.slug, description: level.description || '',
      level_tier: level.level_tier, due_diligence_type: level.due_diligence_type,
      risk_level: level.risk_level, ubo_threshold_percent: level.ubo_threshold_percent,
      require_ubo_kyc: level.require_ubo_kyc, max_ownership_depth: level.max_ownership_depth,
      review_interval_months: level.review_interval_months
    });
    setShowModal(true);
  };

  const getDueDiligenceColor = (type) => ({
    SDD: 'bg-green-100 text-green-800', CDD: 'bg-blue-100 text-blue-800', EDD: 'bg-red-100 text-red-800'
  }[type] || 'bg-gray-100 text-gray-800');

  const getRiskColor = (risk) => ({
    LOW: 'bg-green-100 text-green-800', MEDIUM: 'bg-yellow-100 text-yellow-800',
    HIGH: 'bg-orange-100 text-orange-800', PROHIBITED: 'bg-red-100 text-red-800'
  }[risk] || 'bg-gray-100 text-gray-800');

  const getNodeTypeIcon = (type) => ({
    single_select: '📋', multi_select: '☑️', data_capture: '📝', checks: '🔍',
    router: '🔀', compute: '🧮', finalize: '✅', evidence_request: '📎',
    loop_people: '👥', ownership_traversal: '🏢', workflow_step: '⚙️', instruction: '📢'
  }[type] || '❓');

  const getStageColor = (stage) => {
    const colors = {
      A_INTAKE: '#3B82F6', B_ENTITY_ID: '#10B981', C_BUSINESS_PROFILE: '#F59E0B',
      D_GEOGRAPHY: '#8B5CF6', E_OWNERSHIP: '#EC4899', F_NOMINEE_PROXY: '#EF4444',
      G_PRODUCT_RISK: '#14B8A6', H_FUNDS_WEALTH: '#F97316', I_SCREENING_ESCALATION: '#6366F1', J_DECISION: '#22C55E'
    };
    return colors[stage] || '#6B7280';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">KYB Verification Levels</h1>
          <p className="text-sm text-gray-600 mt-1">Configure business verification levels with decision trees</p>
        </div>
        <button onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <Plus className="w-4 h-4" /> Add Level
        </button>
      </div>

      {/* Levels List */}
      <div className="space-y-4">
        {levels.map((level) => (
          <div key={level.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">{level.name}</h3>
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">Tier {level.level_tier}</span>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getDueDiligenceColor(level.due_diligence_type)}`}>{level.due_diligence_type}</span>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getRiskColor(level.risk_level)}`}>{level.risk_level}</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">{level.description}</p>
                <div className="grid grid-cols-4 gap-4">
                  <div><div className="text-xs text-gray-500">UBO Threshold</div><div className="text-sm font-medium">{level.ubo_threshold_percent}%</div></div>
                  <div><div className="text-xs text-gray-500">UBO KYC</div><div className="text-sm font-medium">{level.require_ubo_kyc ? 'Required' : 'Optional'}</div></div>
                  <div><div className="text-xs text-gray-500">Max Depth</div><div className="text-sm font-medium">{level.max_ownership_depth} levels</div></div>
                  <div><div className="text-xs text-gray-500">Review Interval</div><div className="text-sm font-medium">{level.review_interval_months} months</div></div>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => fetchDecisionTree(level.id)} className="flex items-center gap-1 px-3 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded" title="View Decision Tree">
                  <GitBranch className="w-4 h-4" /> Tree
                </button>
                <button onClick={() => handleEdit(level)} className="flex items-center gap-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded">
                  <Edit2 className="w-4 h-4" /> Edit
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {levels.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No KYB verification levels configured</p>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold">{editingLevel ? 'Edit KYB Level' : 'Add KYB Level'}</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                  <input type="text" value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" rows="2" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tier</label>
                  <input type="number" min="1" max="10" value={formData.level_tier}
                    onChange={(e) => setFormData({ ...formData, level_tier: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Diligence</label>
                  <select value={formData.due_diligence_type} onChange={(e) => setFormData({ ...formData, due_diligence_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="SDD">SDD - Simplified</option>
                    <option value="CDD">CDD - Customer</option>
                    <option value="EDD">EDD - Enhanced</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Risk Level</label>
                  <select value={formData.risk_level} onChange={(e) => setFormData({ ...formData, risk_level: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">UBO Threshold %</label>
                  <input type="number" min="1" max="100" value={formData.ubo_threshold_percent}
                    onChange={(e) => setFormData({ ...formData, ubo_threshold_percent: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Ownership Depth</label>
                  <input type="number" min="1" max="10" value={formData.max_ownership_depth}
                    onChange={(e) => setFormData({ ...formData, max_ownership_depth: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Review Interval (months)</label>
                  <input type="number" min="1" max="60" value={formData.review_interval_months}
                    onChange={(e) => setFormData({ ...formData, review_interval_months: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={formData.require_ubo_kyc}
                  onChange={(e) => setFormData({ ...formData, require_ubo_kyc: e.target.checked })} className="rounded" />
                <span className="text-sm font-medium text-gray-700">Require UBO KYC</span>
              </label>
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  {editingLevel ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Decision Tree Modal */}
      {showDecisionTreeModal && selectedLevel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <div>
                <h2 className="text-xl font-semibold">{selectedLevel.name} - Decision Tree</h2>
                <p className="text-sm text-gray-600">{decisionNodes.length} nodes across {[...new Set(decisionNodes.map(n => n.stage))].length} stages</p>
              </div>
              <button onClick={() => setShowDecisionTreeModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Close</button>
            </div>
            <div className="p-6">
              {/* Stage Legend */}
              <div className="flex flex-wrap gap-2 mb-6">
                {[...new Set(decisionNodes.map(n => n.stage))].map(stage => (
                  <span key={stage} className="px-2 py-1 text-xs rounded-full text-white" style={{ backgroundColor: getStageColor(stage) }}>
                    {stage.replace('_', ' ')}
                  </span>
                ))}
              </div>
              {/* Nodes */}
              <div className="space-y-3">
                {decisionNodes.map((node, idx) => (
                  <div key={node.id} className="border rounded-lg p-4" style={{ borderLeftWidth: '4px', borderLeftColor: getStageColor(node.stage) }}>
                    <div className="flex items-start gap-3">
                      <span className="text-xl">{getNodeTypeIcon(node.question_type)}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">{node.node_id}</span>
                          <span className="text-xs text-gray-500">{node.question_type}</span>
                          {node.auto_validate && <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Auto</span>}
                          {node.next_node_id && <span className="text-xs text-gray-400">→ {node.next_node_id}</span>}
                        </div>
                        <p className="text-sm text-gray-800">{node.question}</p>
                        {node.options && node.options.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {node.options.map((opt, i) => (
                              <span key={i} className="text-xs bg-gray-50 border px-2 py-1 rounded">
                                {opt.label} {opt.score_delta !== 0 && <span className={opt.score_delta > 0 ? 'text-red-600' : 'text-green-600'}>({opt.score_delta > 0 ? '+' : ''}{opt.score_delta})</span>}
                                {opt.hard_stop && <span className="text-red-600 font-bold"> ⛔</span>}
                              </span>
                            ))}
                          </div>
                        )}
                        {node.checks && node.checks.length > 0 && (
                          <div className="mt-2 text-xs text-purple-600">Checks: {node.checks.join(', ')}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KybVerificationLevels;

